import React, { useState } from "react";
import Link from "next/link";

import getArticles from "@/api/requests/getArticles";
import { Article } from "@/custom-types/Article";
import { useEventTracker } from "@/hooks/useEventTracker";
import useOnComponentInView from "@/hooks/useOnComponentInView";

import ArticleCard, {
  ArticleCardPlaceholder,
} from "@/components/botanic/ArticleCard";
import Carousel from "@/components/botanic/Carousel";
import NoResults from "@/components/NoResults";
import Subtitle from "@/components/Subtitle";
import TrackImpression from "@/components/TrackImpression";

type Props = {
  title: string;
  subtitle?: string;
  link?: string;
  linkText?: string;
  apiTags?: string[];
  showTags?: boolean;
  page: string;
  className?: string;
};

const NewsSection = ({
  title,
  subtitle,
  link,
  linkText,
  apiTags,
  showTags = true,
  page,
  className = "",
}: Props) => {
  const [articles, setArticles] = useState([{}, {}, {}]);
  const { publishEvent } = useEventTracker();

  const getData = async () => {
    publishEvent({
      action: "Impression",
      category: "News Carousel",
      label: "News Carousel",
      nonInteractionEvent: true,
    });

    const articles = await getArticles(5, apiTags);
    setArticles(articles);
  };

  const [ref] = useOnComponentInView(getData, {
    freezeOnceVisible: true,
  });

  const SubtitleComponent = subtitle ? (
    <Subtitle title={title} subtitle={subtitle} changeLocation={false} />
  ) : undefined;

  const NoResultsComponent = (
    <NoResults sectionName="news">
      <p className="font-bold text-md">
        We're having trouble grabbing the most recent articles.
      </p>
      <p>
        Visit{" "}
        <Link href="/news" className="no-underline font-bold text-green">
          our news hub
        </Link>{" "}
        for all of the latest cannabis news.
      </p>
    </NoResults>
  );

  return (
    <div ref={ref} className={className}>
      <Carousel
        title={title}
        subtitle={SubtitleComponent}
        seeMoreText={linkText}
        seeMoreHref={link}
        id="news-section"
        showSeeMoreCard={true}
        noResultsDisplay={NoResultsComponent}
        seeMoreOnClick={() => {
          publishEvent({
            action: "Click",
            category: "Carousel",
            label: `${page} news carousel show all`,
          });
        }}
        cardsPerSlide={3}
        className="container"
        seeMoreCardClass="rounded overflow-hidden shadow-low bg-white"
      >
        {articles.map((article: Partial<Article>, slot: number) =>
          article.title ? (
            article.link ? (
              <TrackImpression
                category="News Article Card"
                label={`Slot #${slot + 1}: ${title}`}
                className="h-full"
                key={`article-${slot}`}
              >
                <ArticleCard
                  author={article.author}
                  data-testid="news-section-card"
                  date={article.modifiedGmt}
                  href={article.link}
                  imageProps={{ sizes: [282, null, 300, 322, null, 282] }}
                  imageUrl={article.image}
                  onClick={() => {
                    publishEvent({
                      action: "Click",
                      category: "News Article Card",
                      label: `Slot #${slot + 1}: ${title}`,
                    });
                  }}
                  tags={
                    showTags && article.categories?.[0]
                      ? [article.categories[0], "Article"]
                      : undefined
                  }
                  title={article.title}
                />
              </TrackImpression>
            ) : (
              <></>
            )
          ) : (
            <ArticleCardPlaceholder
              key={slot}
              className="block h-full shadow-low rounded overflow-hidden"
            />
          ),
        )}
      </Carousel>
    </div>
  );
};

export default NewsSection;
